import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Alert, Box, Typography, Divider, Paper, IconButton,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { dictionaryService } from '../../services/api';

// Ajout au dictionnaire d'un mot suggere par un visiteur : le super admin relit, corrige l'orthographe et la
// traduction, puis valide. Le mot est cree « non vérifié » (il apparaît dans « À vérifier seulement »).
const EXAMPLE_FIELDS = [
  { key: 'duala', label: 'Duala' },
  { key: 'francais', label: 'Français' },
  { key: 'anglais', label: 'Anglais' },
  { key: 'espagnol', label: 'Espagnol' },
];

function AddWordDialog({ suggestion, onClose, onAdded }) {
  const isFrench = suggestion.language === 'francais';
  const [form, setForm] = useState({
    duala: isFrench ? '' : suggestion.word,
    grammar: '',
    french: isFrench ? suggestion.word : '',
    english: '',
    spanish: '',
    note: '',
  });
  const [examples, setExamples] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setExample = (i, key, value) => setExamples((prev) => prev.map((ex, idx) => (idx === i ? { ...ex, [key]: value } : ex)));
  const canSave = form.duala.trim() && form.french.trim() && !saving;

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const body = { ...form, examples: examples.filter((ex) => (ex.duala || '').trim()) };
      await dictionaryService.addSuggestedWord(suggestion.id, body);
      onAdded();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.error) || 'Erreur lors de l\'ajout');
      setSaving(false);
    }
  };

  const text = (key, label, props = {}) => (
    <TextField label={label} value={form[key]} onChange={(e) => set(key, e.target.value)} fullWidth size="small" {...props} />
  );

  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle>Ajouter au dictionnaire — <em>{suggestion.word}</em></DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
            Suggestion {isFrench ? 'en français' : 'en duala'}{suggestion.contact_email ? ` (${suggestion.contact_email})` : ''}.
            {suggestion.note ? `\nPrécision du visiteur : ${suggestion.note}` : '\nAucune précision du visiteur.'}
          </Alert>
          <Typography variant="caption" color="text.secondary">
            Corrigez l&apos;orthographe et les traductions avant d&apos;ajouter. Le mot est créé « non vérifié » et reste conservé lors des prochains imports.
            Le mot-vedette doit être définitif : il ne se renomme pas ensuite depuis cet écran.
          </Typography>
          {text('duala', 'Mot duala', { required: true, autoFocus: true })}
          {text('grammar', 'Grammaire (ex. n., v.)')}
          {text('french', 'Français', { required: true, multiline: true, minRows: 2 })}
          {text('english', 'Anglais', { multiline: true, minRows: 2 })}
          {text('spanish', 'Espagnol', { multiline: true, minRows: 2 })}
          {text('note', 'Note', { multiline: true, minRows: 2 })}

          <Divider textAlign="left">Exemples (facultatif)</Divider>
          {examples.map((ex, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2">Exemple {i + 1}</Typography>
                <IconButton size="small" onClick={() => setExamples((prev) => prev.filter((_, idx) => idx !== i))} aria-label="Retirer cet exemple">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              {EXAMPLE_FIELDS.map(({ key, label }) => (
                <TextField key={key} label={label} value={ex[key] || ''} onChange={(e) => setExample(i, key, e.target.value)} size="small" fullWidth multiline />
              ))}
            </Paper>
          ))}
          <Box>
            <Button size="small" startIcon={<AddIcon />} onClick={() => setExamples((prev) => [...prev, {}])}>Ajouter un exemple</Button>
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Annuler</Button>
        <Button variant="contained" onClick={save} disabled={!canSave}>
          {saving ? 'Ajout...' : 'Ajouter au dictionnaire'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddWordDialog;
